import { type ReactElement, useRef, useState } from "react";
// The directory browser.
import { Link, useNavigate } from "react-router";
import useSWR from "swr";
import AlertCircle from "~icons/tabler/alert-circle";
import DotsVertical from "~icons/tabler/dots-vertical";
import File from "~icons/tabler/file";
import FilePlus from "~icons/tabler/file-plus";
import Folder from "~icons/tabler/folder";
import FolderPlus from "~icons/tabler/folder-plus";
import Home from "~icons/tabler/home";
import LayoutGrid from "~icons/tabler/layout-grid";
import LayoutGridAdd from "~icons/tabler/layout-grid-add";
import Upload from "~icons/tabler/upload";
import type { DirectoryEntry } from "./apiTypes.ts";
import {
  apiFetch,
  apiJson,
  getConfig,
  uploadFile,
  useFolderPermissions,
  usePath,
} from "./common.ts";
import ManagementPage from "./ManagementPage.tsx";
import { Modal, type ModalHandle } from "./Modal.tsx";
import UploadProgressToast, { type UploadProgress } from "./UploadProgressToast.tsx";

function EntryCreator(props: {
  label: string;
  icon: ReactElement;
  onCreate: (name: string) => void;
}) {
  const modalRef = useRef<ModalHandle>(null);

  return (
    <>
      <button type="button" onClick={() => modalRef.current?.open()}>
        {props.icon} {props.label}
      </button>
      <Modal
        ref={modalRef}
        title={props.label}
        inputLabel="Name"
        submitLabel="Create"
        onSubmit={props.onCreate}
      />
    </>
  );
}

const fetcher = (url: string) => apiJson<DirectoryEntry[]>(url);

type DownloadAction = "download" | "export";

type UploadItem = { file: File; path: string };

async function collectDroppedEntry(
  entry: FileSystemEntry,
  parent: string,
  files: UploadItem[],
  directories: string[],
): Promise<void> {
  const path = parent ? `${parent}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    files.push({ file, path });
  } else if (entry.isDirectory) {
    directories.push(path);
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    while (true) {
      const children = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (!children.length) break;
      for (const child of children) {
        await collectDroppedEntry(child, path, files, directories);
      }
    }
  }
}

function Breadcrumbs(props: { path: string }) {
  if (!props.path) {
    return <title>LynxKite 2000:MM</title>;
  }

  return (
    <div className="breadcrumbs">
      <Link to="/dir/" aria-label="home">
        <Home />
      </Link>
      <span className="current-folder">
        {props.path
          .split("/")
          .filter(Boolean)
          .map((part, index, parts) => {
            const encodedPartPath = parts
              .slice(0, index + 1)
              .map((segment) => encodeURIComponent(segment))
              .join("/");
            const isLast = index === parts.length - 1;
            return (
              <span key={encodedPartPath}>
                {index > 0 ? <span className="path-delimiter">/</span> : null}
                {isLast ? <span>{part}</span> : <Link to={`/dir/${encodedPartPath}`}>{part}</Link>}
              </span>
            );
          })}
      </span>
      <title>{props.path}</title>
    </div>
  );
}

export default function Directory() {
  const path = usePath().replace(/^[/]$|^[/]dir$|^[/]dir[/]/, "");
  const encodedPath = encodeURIComponent(path || "");
  const config = getConfig();
  const permissions = useFolderPermissions(path || "");
  const canWrite = permissions.write;
  const list = useSWR(`/api/dir/list?path=${encodedPath}`, fetcher, {
    dedupingInterval: 0,
  });
  const navigate = useNavigate();
  const renameModalRef = useRef<ModalHandle>(null);
  const [renameTarget, setRenameTarget] = useState<DirectoryEntry | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [pendingDownload, setPendingDownload] = useState<{
    action: DownloadAction;
    itemName: string;
  } | null>(null);

  function link(item: DirectoryEntry) {
    const encodedName = encodePathSegments(item.name);
    if (item.type === "directory") {
      return `/dir/${encodedName}`;
    }
    if (item.type === "workspace") {
      return `/edit/${encodedName}`;
    }
    return `/code/${encodedName}`;
  }

  function shortName(item: DirectoryEntry) {
    return item.name
      .split("/")
      .pop()
      ?.replace(/[.]lynxkite[.]json$/, "");
  }

  function encodePathSegments(path: string): string {
    const segments = path.split("/");
    return segments.map((segment) => encodeURIComponent(segment)).join("/");
  }

  function newWorkspaceIn(path: string, workspaceName: string) {
    const pathSlash = path ? `${encodePathSegments(path)}/` : "";
    navigate(`/edit/${pathSlash}${encodeURIComponent(workspaceName)}.lynxkite.json`, {
      replace: true,
    });
  }
  function newCodeFile(path: string, name: string) {
    const pathSlash = path ? `${encodePathSegments(path)}/` : "";
    navigate(`/code/${pathSlash}${encodeURIComponent(name)}`, {
      replace: true,
    });
  }
  async function uploadFiles(files: UploadItem[], directories: string[] = []) {
    const dirParam = encodeURIComponent(path || "");
    if (directories.length) {
      const formData = new FormData();
      for (const directory of directories) formData.append("directory", directory);
      const res = await apiFetch(`/api/upload?dir=${dirParam}`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        alert("Failed to upload folders.");
        return;
      }
    }
    const totalBytes = files.reduce((total, item) => total + item.file.size, 0);
    const totalFiles = files.length;
    if (totalFiles) {
      setUploadProgress({ uploadedBytes: 0, totalBytes, completedFiles: 0, totalFiles });
    }
    let completedBytes = 0;
    let completedFiles = 0;
    for (const { file, path: relativePath } of files) {
      try {
        await uploadFile(file, {
          directory: path || "",
          path: relativePath,
          onProgress: (uploadedBytes) => {
            setUploadProgress({
              uploadedBytes: completedBytes + uploadedBytes,
              totalBytes,
              completedFiles,
              totalFiles,
            });
          },
        });
        completedBytes += file.size;
        completedFiles += 1;
        setUploadProgress({
          uploadedBytes: completedBytes,
          totalBytes,
          completedFiles,
          totalFiles,
        });
      } catch {
        setUploadProgress(null);
        list.mutate();
        alert(`Failed to upload ${file.name}.`);
        return;
      }
    }
    setUploadProgress(null);
    list.mutate();
  }

  async function uploadDroppedItems(dataTransfer: DataTransfer) {
    const entries = Array.from(dataTransfer.items)
      .filter((item) => item.kind === "file")
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => entry !== null && entry !== undefined);
    if (!entries.length) {
      await uploadFiles(Array.from(dataTransfer.files, (file) => ({ file, path: file.name })));
      return;
    }
    const files: UploadItem[] = [];
    const directories: string[] = [];
    try {
      for (const entry of entries) {
        await collectDroppedEntry(entry, "", files, directories);
      }
      await uploadFiles(files, directories);
    } catch {
      alert("Failed to read dropped files or folders.");
    }
  }

  async function newFolderIn(path: string, folderName: string) {
    const pathSlash = path ? `${path}/` : "";
    const res = await apiFetch("/api/dir/mkdir", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: pathSlash + folderName }),
    });
    list.mutate();
    if (!res.ok) {
      alert("Failed to create folder.");
    }
  }

  async function deleteItem(item: DirectoryEntry) {
    const confirmationEnabled = localStorage.getItem("lynxkite-delete-confirmation") !== "false";
    if (confirmationEnabled && !window.confirm(`Are you sure you want to delete "${item.name}"?`))
      return;
    const apiPath = item.type === "directory" ? "/api/dir/delete" : "/api/delete";
    await apiFetch(apiPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: item.name }),
    });
    list.mutate();
  }

  function openRenameModal(item: DirectoryEntry) {
    setRenameTarget(item);
    renameModalRef.current?.open(shortName(item) ?? "");
  }

  async function submitRename(newName: string) {
    if (!renameTarget) return;
    const oldParts = renameTarget.name.split("/");
    oldParts.pop();
    const parentPath = oldParts.join("/");
    const targetName = renameTarget.type === "workspace" ? `${newName}.lynxkite.json` : newName;
    const newPath = parentPath ? `${parentPath}/${targetName}` : targetName;
    if (newPath === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    const res = await apiFetch("/api/rename", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ old_path: renameTarget.name, new_path: newPath }),
    });
    if (!res.ok) {
      alert("Failed to rename item.");
      return;
    }
    setRenameTarget(null);
    list.mutate();
  }

  async function downloadBlob(apiPath: string, item: DirectoryEntry, fileName: string) {
    const res = await apiFetch(apiPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: item.name }),
    });
    if (!res.ok) {
      alert("Failed to download file.");
      return false;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return true;
  }

  function closeDropdown(trigger: HTMLElement) {
    const dropdown = trigger.closest(".dropdown");
    if (dropdown?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement).blur();
    }
    trigger.blur();
  }

  async function runDownloadAction(
    action: DownloadAction,
    apiPath: string,
    item: DirectoryEntry,
    fileName: string,
    trigger: HTMLElement,
  ) {
    if (pendingDownload !== null) return;
    setPendingDownload({ action, itemName: item.name });
    try {
      const started = await downloadBlob(apiPath, item, fileName);
      if (started) {
        closeDropdown(trigger);
      }
    } finally {
      setPendingDownload(null);
    }
  }

  if (list.error) {
    return (
      <ManagementPage>
        <div className="directory-error">
          <AlertCircle style={{ verticalAlign: "baseline" }} />
          <p>
            <h1>Error</h1>
            {list.error.message}
          </p>
        </div>
      </ManagementPage>
    );
  }

  return (
    <ManagementPage>
      {list.isLoading && (
        <output className="loading spinner-border">
          <span className="visually-hidden">Loading...</span>
        </output>
      )}

      {list.data && (
        <>
          {canWrite && (
            <div className="actions">
              <EntryCreator
                onCreate={(name) => {
                  newWorkspaceIn(path || "", name);
                }}
                icon={<LayoutGridAdd />}
                label="New workspace"
              />
              {config.enterprise_available && (
                <Link to="/progress">
                  <LayoutGrid /> Enterprise progress
                </Link>
              )}
              <EntryCreator
                onCreate={(name) => {
                  newCodeFile(path || "", name);
                }}
                icon={<FilePlus />}
                label="New code file"
              />
              <EntryCreator
                onCreate={(name: string) => {
                  newFolderIn(path || "", name);
                }}
                icon={<FolderPlus />}
                label="New folder"
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                multiple
                onChange={(e) => {
                  if (e.target.files?.length)
                    uploadFiles(Array.from(e.target.files, (file) => ({ file, path: file.name })));
                  e.target.value = "";
                }}
              />
              <button type="button" onClick={() => fileInputRef.current?.click()}>
                <Upload /> Upload file
              </button>
            </div>
          )}
          {!canWrite && !permissions.isLoading && config.authentication_issuer && (
            <p className="text-sm opacity-70 px-2">This folder is read-only.</p>
          )}
          {!canWrite && config.enterprise_available && (
            <div className="actions">
              <Link to="/progress">
                <LayoutGrid /> Enterprise progress
              </Link>
            </div>
          )}

          <Breadcrumbs path={path} />

          <div
            className={`entry-drop-zone${isDragOver && canWrite ? " drag-over" : ""}`}
            onDragOver={
              canWrite
                ? (e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }
                : undefined
            }
            onDragLeave={
              canWrite
                ? (e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setIsDragOver(false);
                    }
                  }
                : undefined
            }
            onDrop={
              canWrite
                ? (e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    if (e.dataTransfer.items.length || e.dataTransfer.files.length)
                      uploadDroppedItems(e.dataTransfer);
                  }
                : undefined
            }
          >
            {list.data.map(
              (item: DirectoryEntry) =>
                !shortName(item)?.startsWith("__") && (
                  <div key={item.name} className="entry">
                    <Link key={link(item)} to={link(item)}>
                      {item.type === "directory" ? (
                        <Folder />
                      ) : item.type === "workspace" ? (
                        <LayoutGrid />
                      ) : (
                        <File />
                      )}
                      <span className="entry-name">{shortName(item)}</span>
                    </Link>
                    <div className="dropdown dropdown-left dropdown-end">
                      <button
                        className="entry-actions-button"
                        tabIndex={0}
                        type="button"
                        aria-label={`Open actions for ${shortName(item)}`}
                      >
                        <DotsVertical />
                      </button>
                      <ul tabIndex={0} className="dropdown-content menu">
                        {canWrite && (
                          <>
                            <li>
                              <button
                                className="delete-button"
                                type="button"
                                onClick={() => {
                                  deleteItem(item);
                                }}
                              >
                                Delete
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  openRenameModal(item);
                                }}
                              >
                                Rename
                              </button>
                            </li>
                          </>
                        )}
                        {item.type !== "directory" && (
                          <li>
                            <button
                              type="button"
                              aria-disabled={pendingDownload !== null}
                              onClick={async (e) => {
                                await runDownloadAction(
                                  "download",
                                  "/api/download",
                                  item,
                                  item.name.split("/").pop()!,
                                  e.currentTarget,
                                );
                              }}
                            >
                              {pendingDownload?.action === "download" &&
                              pendingDownload.itemName === item.name
                                ? "Downloading..."
                                : "Download"}
                            </button>
                          </li>
                        )}
                        {item.type === "workspace" && (
                          <li>
                            <button
                              type="button"
                              aria-disabled={pendingDownload !== null}
                              onClick={async (e) => {
                                const fileName = `${shortName(item)}.zip`;
                                await runDownloadAction(
                                  "export",
                                  "/api/export_workspace",
                                  item,
                                  fileName,
                                  e.currentTarget,
                                );
                              }}
                            >
                              {pendingDownload?.action === "export" &&
                              pendingDownload.itemName === item.name
                                ? "Exporting..."
                                : "Export static page"}
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                ),
            )}
            {list.data.length === 0 && <div className="entry empty">This folder is empty.</div>}
          </div>
        </>
      )}
      {canWrite && (
        <Modal
          ref={renameModalRef}
          title="Rename item"
          description={renameTarget ? `Current name: ${shortName(renameTarget)}` : ""}
          inputLabel="New name"
          submitLabel="Rename"
          onSubmit={submitRename}
        />
      )}
      {uploadProgress && <UploadProgressToast {...uploadProgress} />}
    </ManagementPage>
  );
}
